/**
 * ⚠ **This is a computed view, not a metric.** The dictionary is the 91 rows of the spreadsheet, and every
 * number elsewhere in the product traces back to one of them. A health score traces back to *three* of them and
 * a formula we made up, so putting it in the catalog would quietly break the property that makes the catalog
 * worth having. It is derived here, in the frontend, from metrics that are collected.
 * ⚠ **The formula below is a starting position, not an answer, and it is designed to be argued with.** Nobody
 * has said what "healthy" means — the request arrived as "overall client health section" with no definition,
 * which is the same shape as `user_classification`'s four bands, still unbuilt because the thresholds never
 * came. Shipping a single opaque number would repeat that: a score nobody can explain is a score nobody can
 * correct. So all three inputs and their weights are shown on the card, and the weights are equal because
 * there is no evidence yet for any other split — "equal until someone tells us otherwise" is a defensible
 * position in a way that an invented 0.5/0.3/0.2 is not.
 */

export type HealthComponent = {
  key: "adoption" | "stickiness" | "direction"
  label: string
  /** 0–100, or `null` when its inputs are missing. **Never substituted with a default** — see `summarise` */
  score: number | null
  detail: string
}

export type HealthInput = {
  adoptionRates: readonly number[]
  avgDau: number | null
  /** MAU — ⚠ must come from its **own** query at month granularity; see `useClientHealth` */
  mau: number | null
  dauNow: number | null
  dauPrev: number | null
}

const clamp = (n: number) => Math.max(0, Math.min(100, n))
const round = (n: number) => Math.round(n * 10) / 10

export function directionScore(now: number | null, prev: number | null): number | null {
  if (now == null || prev == null) return null
  // No previous activity at all: a rise from nothing is real, but a ratio cannot express it
  if (prev === 0) return now > 0 ? 100 : null
  return clamp(((now / prev - 0.5) / 1) * 100)
}

export function buildComponents(input: HealthInput): HealthComponent[] {
  const rates = input.adoptionRates.filter((n) => Number.isFinite(n))
  const adoption =
    rates.length > 0 ? clamp((rates.reduce((a, b) => a + b, 0) / rates.length) * 100) : null

  const stickiness =
    input.avgDau != null && input.mau != null && input.mau > 0
      ? clamp((input.avgDau / input.mau) * 100)
      : null

  const direction = directionScore(input.dauNow, input.dauPrev)

  return [
    {
      key: "adoption",
      label: "Module adoption",
      score: adoption == null ? null : round(adoption),
      detail:
        rates.length > 0
          ? `mean of ${rates.length} modules' "active users ÷ users with access"`
          : "no module adoption data in this period",
    },
    {
      key: "stickiness",
      label: "Stickiness",
      score: stickiness == null ? null : round(stickiness),
      detail:
        input.avgDau != null && input.mau != null && input.mau > 0
          ? `${round(input.avgDau)} average daily active ÷ ${input.mau} monthly active`
          : "needs both daily and monthly active users",
    },
    {
      key: "direction",
      label: "Direction",
      score: direction == null ? null : round(direction),
      detail:
        input.dauNow != null && input.dauPrev != null
          ? `${input.dauNow} active-user-days vs ${input.dauPrev} in the period before`
          : "no previous period to compare with",
    },
  ]
}

/**
 * ⚠ **A missing component is left out of the average rather than counted as zero.** A customer whose MAU has
 * not been collected yet is not an unhealthy customer, and scoring them as one would send CS after the wrong
 * accounts — the exact failure the four-state tile contract exists to prevent, in a different costume. The
 * card says how many of the three were available.
 */
export function summarise(components: readonly HealthComponent[]): {
  score: number | null
  available: number
  total: number
} {
  const scored = components.filter((c) => c.score != null)
  return {
    score: scored.length === 0 ? null : round(scored.reduce((a, c) => a + (c.score ?? 0), 0) / scored.length),
    available: scored.length,
    total: components.length,
  }
}

/** Four bands, named rather than coloured alone — the palette's status colours never carry meaning by themselves */
export function healthBand(score: number | null): { label: string; tone: "good" | "warning" | "serious" | "unknown" } {
  if (score == null) return { label: "Not enough data", tone: "unknown" }
  if (score >= 60) return { label: "Healthy", tone: "good" }
  if (score >= 30) return { label: "Watch", tone: "warning" }
  return { label: "At risk", tone: "serious" }
}
